import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import { updateSupplier } from '../../../../../../redux/services/suppliersService';
import { openDillToast } from '../../../../../../utils/helpers';
import { Supplier } from '@dill/dill-shared';
import { getUserDetails } from '../../../../../../redux/services/authService';
import { DillButton, DillToggle } from '../../../../../general/Dill';
import { Tooltip as ReactTooltip } from 'react-tooltip';

interface CreditMemoSettings {
  agedCreditMemos: boolean;
  discountOnCredit: boolean;
}

interface CreditMemoSettingsSectionProps {
  supplier?: Supplier | null;
}

const getInitialSettings = (supplier?: Supplier | null): CreditMemoSettings => ({
  agedCreditMemos: supplier?.arSettings?.agedCreditMemos ?? false,
  discountOnCredit: supplier?.arSettings?.discountOnCredit ?? false,
});

const isDiscountOnCreditDisabled = (supplier?: Supplier | null) =>
  !(
    supplier?.integrationType === 'DDI_MANUAL_UPLOAD' ||
    supplier?.integrationType === 'MARKS_MANUAL_UPLOAD' ||
    (supplier?.integrationType === 'P21_MANUAL_UPLOAD' &&
      supplier?.domainId === 'carolinasupply') ||
    (supplier?.integrationType === 'SAGE_100' && supplier?.domainId === 'topgun') ||
    supplier?.integrationType === 'DISTRIBUTION_ONE'
  ) && process.env.REACT_APP_ENVIRONMENT === 'production';

export const CreditMemoSettingsSection = ({ supplier }: CreditMemoSettingsSectionProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const canEdit = useMemo(() => {
    if (!currentUser?.email || !supplier) return false;
    const email = currentUser.email.toLowerCase();
    const supplierEmail = supplier.email?.toLowerCase() ?? '';
    const adminEmails = (supplier.adminSupplierEmails ?? []).map((e) => e.toLowerCase());
    return (
      email === supplierEmail ||
      adminEmails.includes(email) ||
      (currentUser.roles?.includes('DILL_ADMIN') ?? false)
    );
  }, [currentUser, supplier]);

  const [settings, setSettings] = useState<CreditMemoSettings>(() => getInitialSettings(supplier));
  const [initialSettings, setInitialSettings] = useState<CreditMemoSettings>(() =>
    getInitialSettings(supplier)
  );
  const [saving, setSaving] = useState(false);
  const discountDisabled = useMemo(() => isDiscountOnCreditDisabled(supplier), [supplier]);

  useEffect(() => {
    const newSettings = getInitialSettings(supplier);
    setSettings(newSettings);
    setInitialSettings(newSettings);
  }, [supplier]);

  const settingsChanged = useMemo(
    () =>
      settings.agedCreditMemos !== initialSettings.agedCreditMemos ||
      settings.discountOnCredit !== initialSettings.discountOnCredit,
    [settings, initialSettings]
  );

  const handleSave = async () => {
    setSaving(true);
    const result = await dispatch(
      updateSupplier({
        supplierId: supplier?.id || '',
        supplierDetails: {
          arSettings: {
            agedCreditMemos: settings.agedCreditMemos,
            discountOnCredit: settings.discountOnCredit,
          },
        },
      })
    );
    setSaving(false);

    if (result.type === 'supplier/update/fulfilled') {
      await dispatch(getUserDetails());
      openDillToast({ type: 'SUCCESS', message: 'Credit memo settings saved' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to save credit memo settings' });
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900">Credit Memo Settings</h4>
        <p className="text-xs text-gray-400 mt-0.5">Configure how credit memos are handled.</p>
      </div>

      <div className="flex flex-col gap-1 flex-1">
        <label className="flex items-center justify-between gap-4 py-3 cursor-pointer">
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-800">Age Credit Memos to Date</p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Include credit memos in aging buckets based on their due/issue date, instead of
              applied to oldest aging category.
            </p>
          </div>
          <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
            <DillToggle
              value={settings.agedCreditMemos}
              onChange={() =>
                setSettings((prev) => ({ ...prev, agedCreditMemos: !prev.agedCreditMemos }))
              }
              isDisabled={!canEdit}
            />
          </div>
        </label>

        <label className="flex items-center justify-between gap-4 py-3 cursor-pointer">
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-800">Discounts on Credits</p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Apply early payment discounts to credit memos paid before the discount deadline.
            </p>
          </div>
          <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : 'discount-on-credit-tooltip'}>
            <DillToggle
              value={settings.discountOnCredit}
              onChange={() =>
                setSettings((prev) => ({ ...prev, discountOnCredit: !prev.discountOnCredit }))
              }
              isDisabled={discountDisabled || !canEdit}
            />
          </div>
          {canEdit && discountDisabled && (
            <ReactTooltip
              id="discount-on-credit-tooltip"
              place="top"
              variant="dark"
              opacity="100%"
              style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '240px' }}>
              Contact customer support to enable this setting.
            </ReactTooltip>
          )}
        </label>
      </div>

      <div className="mt-4 flex justify-end">
        <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
          <DillButton
            onClick={handleSave}
            label="Save changes"
            isLoading={saving}
            isDisabled={!canEdit || !settingsChanged}
          />
        </div>
      </div>

      {!canEdit && (
        <ReactTooltip
          id="no-permission-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          You do not have permission to make changes
        </ReactTooltip>
      )}
    </div>
  );
};
